import { Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { useAdminChangeUserPassword } from '@admin/hooks';
import { PasswordInput } from '@components';
import { passwordSchema } from '@validators';

export const ChangePasswordModal = ({ user, onHide }) => {
  const [formError, setFormError] = useState<string | null>(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const changePasswordMutation = useAdminChangeUserPassword();

  const form = useForm({
    initialValues: { password: '' },
    validate: zodResolver(
      z.object({
        password: passwordSchema,
      }),
    ),
    validateInputOnBlur: true,
  });

  const handleSubmit = async (values) => {
    setFormError(null);
    setPendingValues(values);
    setShowNotifyModal(true);
  };

  const handleNotifyConfirm = async () => {
    try {
      await changePasswordMutation.mutateAsync({ params: { userId: user._id }, body: { password: pendingValues.password } });
      setShowNotifyModal(false);
      onHide();
    } catch (error: any) {
      setFormError(error.message || 'Failed to change password');
      setShowNotifyModal(false);
    }
  };

  const handleNotifyCancel = () => {
    setShowNotifyModal(false);
  };

  return (
    <>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md" my="xl">
          <PasswordInput {...form.getInputProps('password')} label="New Password" withAsterisk type="password" size="lg" />
          <Text size="sm" c="dimmed">
            Password must be at least 8 characters, contain uppercase, lowercase, a digit, and a special character.
          </Text>
        </Stack>
        {formError && (
          <Text c="red" fw="bold" mt="md">
            {formError}
          </Text>
        )}
        <Group justify="center" mt="xl">
          <Button type="button" onClick={onHide} disabled={changePasswordMutation.isPending} size="lg" variant="transparent">
            Cancel
          </Button>
          <Button
            disabled={!form.isValid() || changePasswordMutation.isPending}
            type="submit"
            leftSection={changePasswordMutation.isPending && <Loader color="white" />}
            variant="filled"
            size="lg"
          >
            Save
          </Button>
        </Group>
      </form>
      <Modal opened={showNotifyModal} onClose={handleNotifyCancel} title="Notify User" centered withCloseButton={false} zIndex={1000}>
        <Text mb="md">Did you notify the user about this password change?</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={handleNotifyCancel}>
            No
          </Button>
          <Button variant="filled" onClick={handleNotifyConfirm} loading={changePasswordMutation.isPending}>
            Yes
          </Button>
        </Group>
      </Modal>
    </>
  );
};
